import { LitElement, html, css } from 'lit';
import { sharedStyles, stateOf } from './base.js';
import { fmt } from '../fmt.js';

// <gt-stat> — a KPI tile and the canonical live-update surface.
//
// SSE contract: set the reactive properties from your EventSource handler and
// the tile re-renders its own shadow subtree in place (nothing else moves):
//     el.value = payload.price;      // -> re-render
//     el.delta = payload.change_pct;
//
// Attributes / properties:
//   label       small uppercase caption
//   value       the primary number (or pre-formatted string)
//   format      one of GT.fmt keys: money|price|num|int|compact|aum|pct|
//               pctPoints|pctPlain  (applied to a numeric value; omit for raw)
//   delta       signed change; colored + arrowed
//   deltaformat pct (fraction) | pctPoints | plain   (default: pctPoints)
//   state       up|down|flat to force color; otherwise derived from delta
export class GtStat extends LitElement {
  static properties = {
    label: { type: String },
    value: {},
    format: { type: String },
    delta: {},
    deltaformat: { type: String },
    state: { type: String },
  };

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
      }
      .value {
        font-size: var(--fs-xl, 1.6rem);
        font-weight: 600;
        line-height: 1.1;
        color: var(--text, #e2e8f0);
        margin-top: 4px;
      }
      .delta {
        font-size: var(--fs-sm, 0.82rem);
        font-weight: 600;
        margin-top: 2px;
      }
      .delta::before {
        margin-right: 3px;
      }
      .delta.up::before {
        content: '▲';
        font-size: 0.7em;
      }
      .delta.down::before {
        content: '▼';
        font-size: 0.7em;
      }
    `,
  ];

  _fmtValue() {
    const v = this.value;
    if (v == null || v === '') return fmt.DASH;
    const fn = this.format && fmt[this.format];
    return fn ? fn(v) : String(v);
  }

  _fmtDelta() {
    const d = this.delta;
    if (d == null || d === '') return null;
    const kind = this.deltaformat || 'pctPoints';
    if (kind === 'pct') return fmt.pct(d);
    if (kind === 'plain') return (+d > 0 ? '+' : '') + d;
    return fmt.pctPoints(d);
  }

  render() {
    const st = this.state || stateOf(this.delta);
    const deltaText = this._fmtDelta();
    return html`
      ${this.label ? html`<div class="label">${this.label}</div>` : null}
      <div class="value mono">${this._fmtValue()}</div>
      ${deltaText != null
        ? html`<div class="delta mono ${st}">${deltaText}</div>`
        : null}
    `;
  }
}

customElements.define('gt-stat', GtStat);
